import CommonModal from "@/components/common/CommonModal";
import { Button } from "@/components/ui/button";
import { Lock, Loader2 } from "lucide-react";
import moment from "moment";

interface LockAttendanceConfirmModalProps {
  show: boolean;
  attendanceStartDate: string;
  attendanceEndDate: string;
  selectedCount: number;
  isAllSelected: boolean;
  isLocking: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const LockAttendanceConfirmModal = ({
  show,
  attendanceStartDate,
  attendanceEndDate,
  selectedCount,
  isAllSelected,
  isLocking,
  onClose,
  onConfirm,
}: LockAttendanceConfirmModalProps) => {
  const formattedStart = attendanceStartDate
    ? moment(attendanceStartDate).format("DD MMM YYYY")
    : "—";
  const formattedEnd = attendanceEndDate
    ? moment(attendanceEndDate).format("DD MMM YYYY")
    : "—";
  const cycleMonth = attendanceStartDate
    ? moment(attendanceStartDate).format("MMMM YYYY")
    : "";

  return (
    <CommonModal
      show={show}
      onClose={onClose}
      title=""
      className="sm:max-w-md rounded-2xl p-0 overflow-hidden"
    >
      <div className="p-6">
        <div className="flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 mb-4 border border-rose-100">
            <Lock className="text-rose-600 w-7 h-7" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-1.5 font-heading">
            Confirm Lock Attendance
          </h2>
          <p className="text-xs text-gray-500 leading-relaxed max-w-sm mb-4">
            Are you sure you want to lock attendance records for the selected salary cycle?
          </p>

          <div className="w-full bg-slate-50 p-3.5 rounded-xl border border-slate-200/80 space-y-2 text-xs text-left mb-6">
            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-500 font-medium">Cycle Month:</span>
              <span className="font-bold text-slate-900">
                {cycleMonth} Cycle
              </span>
            </div>

            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-500 font-medium">Attendance Period:</span>
              <span className="font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                {formattedStart} &nbsp;➔&nbsp; {formattedEnd}
              </span>
            </div>

            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-500 font-medium">Target Scope:</span>
              <span className="font-semibold text-slate-800">
                {isAllSelected
                  ? "All Branch Users"
                  : selectedCount > 0
                    ? `${selectedCount} Selected Employee${selectedCount > 1 ? "s" : ""}`
                    : "All Verified Branch Users"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLocking}
              className="flex-1 rounded-xl h-10 font-semibold text-xs cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onConfirm}
              disabled={isLocking}
              className="flex-1 rounded-xl h-10 font-semibold text-xs bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center gap-2 cursor-pointer"
            >
              {isLocking ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  Locking...
                </>
              ) : (
                "Confirm & Lock"
              )}
            </Button>
          </div>
        </div>
      </div>
    </CommonModal>
  );
};

export default LockAttendanceConfirmModal;
